/**
 * Where a role's portal begins.
 *
 * One function rather than a literal repeated across the shell, the guard and the
 * brand link: those three must agree, and the only way to be sure they do is for
 * there to be one answer.
 *
 * Three answers now, and each is the only one that works for its role. `SUPER_ADMIN`
 * administers client companies, so the companies screen. `SUPPORT` administers
 * nothing — that screen would refuse it — and the Panel is behind the tenant guard,
 * which would bounce it straight back out; the document list is the one surface that
 * is genuinely its own. Everyone else starts on the Panel.
 */

import type { CurrentPrincipal } from '@deca/api'

import { abilitiesFor } from './roles.ts'

// Narrowed to the two literals it can actually return, not `string` — that keeps
// every `<Link to={landingPathFor(...)}>` call site checked against the router's
// path union, the same as a call site that writes the literal by hand. `string`
// would compile against any path, typo included.
export function landingPathFor(
  principal: CurrentPrincipal | null | undefined,
): '/' | '/decas' | '/admin/empresas' {
  const abilities = abilitiesFor(principal)
  if (abilities.platformAdmin) return '/admin/empresas'
  // Ordered, not exclusive: `choosesOrganisation` is true for SUPER_ADMIN too, and it is the
  // administration screen that is its home. Only the role that reaches across companies **and**
  // administers none lands on the list.
  if (abilities.choosesOrganisation) return '/decas'
  return '/'
}
